import * as React from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { isEmpty } from 'lodash';
import { getSelectableServices, getSelectedServices } from 'selectors/services';

import { useBusiness } from 'hooks/business/useBusiness';
import { usePreferences } from 'hooks/preferences/usePreferences';
import { useReviewsCarousel } from 'hooks/review/useReviewsCarousel';
import { useServices } from 'hooks/services/useServices';
import { useWebsite } from 'hooks/website/useWebsite';

import { TReview } from 'types/review';
import { TService } from 'types/service';

export const sortByDescRating = (a: TReview, b: TReview) =>
  a.rating > b.rating ? -1 : 1;

export const MAX_REVIEWS = 5;

type Props = {
  maxReviewsCount?: number;
};

export const useHomePage = ({ maxReviewsCount = MAX_REVIEWS }: Props = {}) => {
  const {
    home: {
      hero: { image: heroImageSrc },
    },
  } = useWebsite();

  const {
    aboutLink,
    currentReviewIndex,
    reviews,
    showNextReview,
    showPrevReview,
    showReviews,
  } = useReviewsCarousel({ maxCount: maxReviewsCount });

  const { isMultiUser } = useBusiness();
  const { areGiftCardsEnabled } = usePreferences();
  const { selectService } = useServices();
  const router = useRouter();

  const services = useSelector(getSelectableServices);
  const selectedServices = useSelector(getSelectedServices);

  const bookNowLinkUrl =
    isMultiUser && isEmpty(selectedServices) ? '/booking-flow' : '/services';

  const [pendingSelectService, setPendingSelectService] =
    React.useState<TService>();

  const handleSelectService = (
    serviceId: string,
    optionId?: string,
    bookingOder?: number,
  ) => {
    selectService(serviceId, undefined, optionId, bookingOder);
    void router.push('/services');
  };

  const onServiceClick = (id: string) => {
    const service = services.find((service: TService) => service.guid === id);

    if (service)
      if (Boolean(service.options?.length)) {
        setPendingSelectService(service);
      } else {
        handleSelectService(id);
      }
  };

  const onSelectServiceOption = (
    id: string,
    optionId: string,
    bookingOrder?: number,
  ) => {
    handleSelectService(id, optionId, bookingOrder);
  };

  const onCloseServiceOption = () => {
    setPendingSelectService(undefined);
  };

  return {
    areGiftCardsEnabled,
    reviews,
    currentReviewIndex,
    pendingSelectService,
    onSelectServiceOption,
    onCloseServiceOption,
    showNextReview,
    showPrevReview,
    showReviews,
    services,
    onServiceClick,
    bookNowLinkUrl,
    heroImageSrc,
    aboutLink,
  };
};
